import { Controller } from "@hotwired/stimulus";
import { dispatchStampSpend } from "../utils/stampEvents";

/**
 * Stamp Form Controller
 * Handles form submissions that deduct stamps, showing animation before redirect.
 *
 * This controller intercepts form submission, makes an AJAX request,
 * triggers the stamp animation, then redirects to the response location.
 *
 * Usage:
 *   = form_with url: some_path, method: :post, data: { controller: 'stamp-form', stamp_form_cost_value: 3, stamp_form_reason_value: 'daily_challenge' } do
 *     = submit_tag 'Start', data: { stamp_form_target: 'submit' }
 *
 * The server should respond with JSON: { redirect_to: '/path', stamps: 42, stamp_cost: 3 }
 * Or with a redirect (302) which will be followed.
 */

type StampReason =
  | "belt_entry"
  | "daily_challenge"
  | "weekly_challenge"
  | "game_entry"
  | "other";

export default class extends Controller<HTMLFormElement> {
  static targets = ["submit"];
  static values = {
    cost: { type: Number, default: 0 },
    reason: { type: String, default: "other" },
  };

  declare readonly submitTarget: HTMLButtonElement;
  declare readonly hasSubmitTarget: boolean;
  declare costValue: number;
  declare reasonValue: string;

  private isSubmitting = false;
  // Bound once: a fresh .bind(this) in removeEventListener never matches, so the listener stayed
  private boundHandleSubmit = this.handleSubmit.bind(this);

  connect() {
    this.element.addEventListener("submit", this.boundHandleSubmit);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.boundHandleSubmit);
  }

  async handleSubmit(event: Event) {
    event.preventDefault();

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    // Show loading state
    if (this.hasSubmitTarget) {
      this.submitTarget.classList.add("loading");
      this.submitTarget.disabled = true;
    }

    try {
      const formData = new FormData(this.element);
      const response = await fetch(this.element.action, {
        method: this.element.method || "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (response.ok) {
        const contentType = response.headers.get("content-type");

        if (contentType?.includes("application/json")) {
          const data = await response.json();

          // Trigger stamp animation
          if (data.stamp_cost && data.stamp_cost > 0) {
            dispatchStampSpend(
              data.stamp_cost,
              data.stamps,
              this.reasonValue as StampReason
            );

            // Wait for animation to be visible before redirect
            await this.delay(800);
          }

          // Redirect
          if (data.redirect_to) {
            window.location.href = data.redirect_to;
          }
        } else if (response.redirected) {
          // Handle redirect response (no JSON)
          // Try to get stamp info from response headers or just redirect
          window.location.href = response.url;
        }
      } else {
        // Handle error - could be JSON with error message
        const contentType = response.headers.get("content-type");
        if (contentType?.includes("application/json")) {
          const data = await response.json();
          if (data.error) {
            this.showError(data.error);
          }
        } else {
          // Fallback - submit form normally for Rails to handle flash messages
          this.element.submit();
        }
      }
    } catch (error) {
      console.error("Stamp form submission error:", error);
      // Fallback to normal form submission
      this.element.submit();
    } finally {
      this.isSubmitting = false;
      if (this.hasSubmitTarget) {
        this.submitTarget.classList.remove("loading");
        this.submitTarget.disabled = false;
      }
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private showError(message: string) {
    // Could show a toast or alert
    alert(message);
  }
}
